// Central SEO / GEO configuration for KANN.
import { getAlt } from "./imageSeo";
// Public site URL — set REACT_APP_SITE_URL for a custom domain; falls back to the backend/preview URL.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const OG_IMAGE = `${SITE_URL}/og-image.png?v=2`;
export const LANGS = ["nl", "en", "fr", "de"];
export const DEFAULT_LANG = "nl";

export const BUSINESS = {
  legalName: "KannPrint",
  name: "KANN",
  email: "info@kannprint.com",
  phone: "+32 497 22 02 21",
  vat: "BE0763709902",
  street: "Bevrijdingsstraat 68",
  postalCode: "2960",
  city: "Sint-Lenaarts",
  region: "Antwerpen",
  country: "BE",
  countryName: "Belgium",
  sameAs: [
    "https://www.instagram.com/kannprint",
    "https://www.facebook.com/kannprint",
  ],
};

// Countries KANN ships to (used in schema areaServed / GEO targeting).
export const SHIP_COUNTRIES = [
  "AT", "BE", "BG", "HR", "CY", "DK", "FI", "FR", "DE", "GR", "HU", "IE",
  "IT", "LU", "MT", "NL", "PL", "PT", "RO", "ES", "SE",
];

// Google-friendly slug: lowercase, ASCII, hyphen-separated, audience language.
export function slugify(s) {
  return String(s || "").toLowerCase().trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").replace(/[\s-]+/g, "-").replace(/^-+|-+$/g, "");
}

// Organization + WebSite JSON-LD (site-wide identity for search + AI engines).
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    url: SITE_URL,
    logo: OG_IMAGE,
    email: BUSINESS.email,
    telephone: BUSINESS.phone,
    vatID: BUSINESS.vat,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.street,
      postalCode: BUSINESS.postalCode,
      addressLocality: BUSINESS.city,
      addressRegion: BUSINESS.region,
      addressCountry: BUSINESS.country,
    },
    areaServed: SHIP_COUNTRIES.map((c) => ({ "@type": "Country", identifier: c })),
    sameAs: BUSINESS.sameAs,
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: BUSINESS.name,
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: LANGS,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/shop?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

// Shipping + return defaults (mirror backend DEFAULT_SETTINGS).
const SHIPPING = { be: "8.59", eu: "19.97" };
const RETURN_DAYS = 14;
const EU_ONLY = SHIP_COUNTRIES.filter((c) => c !== "BE");

export function returnPolicy() {
  return {
    "@type": "MerchantReturnPolicy",
    applicableCountry: SHIP_COUNTRIES,
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: RETURN_DAYS,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnShippingFees",
  };
}

function shippingDetail(rate, countries, transitMin, transitMax) {
  return {
    "@type": "OfferShippingDetails",
    shippingRate: { "@type": "MonetaryAmount", value: rate, currency: "EUR" },
    shippingDestination: { "@type": "DefinedRegion", addressCountry: countries },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
      transitTime: { "@type": "QuantitativeValue", minValue: transitMin, maxValue: transitMax, unitCode: "DAY" },
    },
  };
}

// Full Product schema with Offer (price, availability, return + shipping policies).
export function productSchema(product, { soldOut } = {}) {
  const first = product.images?.[0];
  const img = first ? (first.startsWith("http") ? first : `${SITE_URL}${first}`) : OG_IMAGE;
  const images = (product.images || [])
    .filter(Boolean)
    .map((s) => (s.startsWith("http") ? s : `${SITE_URL}${s}`));
  const priceValidUntil = `${new Date().getFullYear() + 1}-12-31`;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description || product.tagline || product.name,
    image: (images.length ? images : [img]).map((url, i) => ({ "@type": "ImageObject", url, caption: getAlt(product, i, "en") })),
    sku: product.sku || product.id,
    mpn: product.mpn || product.sku || product.id,
    ...(product.gtin ? { gtin: product.gtin } : {}),
    category: product.category,
    ...(product.tags && product.tags.length ? { keywords: (product.tags || []).join(", ") } : {}),
    material: "PLA (3D-printed)",
    brand: { "@type": "Brand", name: product.brand || BUSINESS.name },
    manufacturer: { "@type": "Organization", name: BUSINESS.legalName },
    ...((product.specifications || []).filter((s) => s.label && s.value).length
      ? {
          additionalProperty: (product.specifications || [])
            .filter((s) => s.label && s.value)
            .map((s) => ({ "@type": "PropertyValue", name: s.label, value: s.value })),
        }
      : {}),
    ...(product.review_count
      ? { aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.review_count } }
      : {}),
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/product/${product.slug || product.id}`,
      priceCurrency: "EUR",
      price: Number(product.price).toFixed(2),
      priceValidUntil,
      availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: BUSINESS.name },
      hasMerchantReturnPolicy: returnPolicy(),
      shippingDetails: [
        shippingDetail(SHIPPING.be, "BE", 1, 3),
        shippingDetail(SHIPPING.eu, EU_ONLY, 2, 5),
      ],
    },
  };
}

// LocalBusiness identity (local SEO — maps, "near me", knowledge panel).
export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Store",
    "@id": `${SITE_URL}/#localbusiness`,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    image: OG_IMAGE,
    url: SITE_URL,
    email: BUSINESS.email,
    telephone: BUSINESS.phone,
    priceRange: "€€",
    currenciesAccepted: "EUR",
    paymentAccepted: "Credit Card, iDEAL, Bancontact",
    vatID: BUSINESS.vat,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.street,
      postalCode: BUSINESS.postalCode,
      addressLocality: BUSINESS.city,
      addressRegion: BUSINESS.region,
      addressCountry: BUSINESS.country,
    },
    areaServed: SHIP_COUNTRIES.map((c) => ({ "@type": "Country", identifier: c })),
    sameAs: BUSINESS.sameAs,
  };
}

// BlogPosting schema for guide articles.
export function articleSchema(article) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt || article.seo_description || article.title,
    image: article.cover_image || OG_IMAGE,
    datePublished: article.created_at,
    dateModified: article.created_at,
    inLanguage: article.language || "nl",
    author: { "@type": "Organization", name: BUSINESS.name, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: BUSINESS.name,
      logo: { "@type": "ImageObject", url: OG_IMAGE },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${article.slug}` },
    keywords: (article.tags || []).join(", "),
  };
}
